import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDispatch, RootState } from "configureStore";
import { socket, socketGet } from "services/socket-client";
import { downloadOrderZip, fetchDownloadOrder } from "./document-cart.service";
import {
  applyCartSnapshot,
  fetchCartThunk,
  setCheckoutStatus,
} from "./documentCart.slice";

const DOWNLOAD_ORDER_POLL_INTERVAL_MS = 3_000;

interface CartUpdatedPayload {
  companyId: string;
  count: number;
  availableCount?: number;
  items?: any[];
  totalSizeBytes?: number;
}

interface DownloadOrderReadyPayload {
  orderId: string;
  orderNumber: string;
  downloadUrl?: string;
}

interface DownloadOrderFailedPayload {
  orderId: string;
  message?: string;
}

interface DownloadOrderProgressPayload {
  orderId: string;
  orderNumber?: string;
  status: string;
  percent?: number;
}

type PendingDownloadOrderEvent =
  | { type: "ready"; payload: DownloadOrderReadyPayload }
  | { type: "failed"; payload: DownloadOrderFailedPayload }
  | { type: "progress"; payload: DownloadOrderProgressPayload };

function triggerZipDownload(
  companyId: string,
  orderId: string,
  orderNumber?: string
) {
  return downloadOrderZip(
    companyId,
    orderId,
    orderNumber ? `${orderNumber}.zip` : undefined
  );
}

export function useDocumentCartSocket(companyId: string | undefined) {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const checkoutOrderId = useSelector(
    (state: RootState) => state.documentCartReducer.checkoutOrderId
  );
  const checkoutLoading = useSelector(
    (state: RootState) => state.documentCartReducer.checkoutLoading
  );
  const checkoutStatus = useSelector(
    (state: RootState) => state.documentCartReducer.checkoutStatus
  );
  const checkoutOrderIdRef = useRef(checkoutOrderId);
  const checkoutLoadingRef = useRef(checkoutLoading);
  const checkoutStatusRef = useRef(checkoutStatus);
  const pendingDownloadOrderEventsRef = useRef<
    Record<string, PendingDownloadOrderEvent>
  >({});

  checkoutOrderIdRef.current = checkoutOrderId;
  checkoutLoadingRef.current = checkoutLoading;
  checkoutStatusRef.current = checkoutStatus;

  const handleReady = useCallback(
    (payload: DownloadOrderReadyPayload) => {
      if (!companyId) return;
      // Sync checkout already started the same-page download from CartDrawer.
      if (
        checkoutOrderIdRef.current &&
        String(payload.orderId) === String(checkoutOrderIdRef.current) &&
        checkoutStatusRef.current === "ready"
      ) {
        return;
      }
      checkoutStatusRef.current = "ready";
      dispatch(
        setCheckoutStatus({
          status: "ready",
          orderId: payload.orderId,
          orderNumber: payload.orderNumber,
        })
      );
      toast.success(
        t("document_cart_download_ready", { number: payload.orderNumber }),
        {
          action: {
            label: t("download"),
            onClick: () => {
              void triggerZipDownload(
                companyId,
                payload.orderId,
                payload.orderNumber
              ).catch(() => toast.error(t("document_cart_download_failed")));
            },
          },
        }
      );
      // Async path: save ZIP on this page (no new tab).
      void triggerZipDownload(
        companyId,
        payload.orderId,
        payload.orderNumber
      ).catch(() => toast.error(t("document_cart_download_failed")));
    },
    [companyId, dispatch, t]
  );

  const handleFailed = useCallback(
    (payload: DownloadOrderFailedPayload) => {
      checkoutStatusRef.current = "failed";
      dispatch(
        setCheckoutStatus({
          status: "failed",
          orderId: payload.orderId,
          error: payload.message,
        })
      );
      toast.error(t("document_cart_download_failed"));
    },
    [dispatch, t]
  );

  const handleProgress = useCallback(
    (payload: DownloadOrderProgressPayload) => {
      if (
        checkoutStatusRef.current === "ready" ||
        checkoutStatusRef.current === "failed"
      ) {
        return;
      }
      dispatch(
        setCheckoutStatus({
          status: "processing",
          orderId: payload.orderId,
        })
      );
    },
    [dispatch]
  );

  const applyDownloadOrderEvent = useCallback(
    (event: PendingDownloadOrderEvent) => {
      if (event.type === "ready") {
        handleReady(event.payload);
      } else if (event.type === "failed") {
        handleFailed(event.payload);
      } else {
        handleProgress(event.payload);
      }
    },
    [handleFailed, handleProgress, handleReady]
  );

  const queuePendingDownloadOrderEvent = useCallback(
    (event: PendingDownloadOrderEvent) => {
      const orderId = String(event.payload.orderId);
      const pendingEvent = pendingDownloadOrderEventsRef.current[orderId];

      if (
        event.type === "progress" &&
        pendingEvent &&
        pendingEvent.type !== "progress"
      ) {
        return;
      }

      pendingDownloadOrderEventsRef.current[orderId] = event;
    },
    []
  );

  useEffect(() => {
    if (!checkoutOrderId) return;

    const orderId = String(checkoutOrderId);
    const pendingEvent = pendingDownloadOrderEventsRef.current[orderId];
    if (!pendingEvent) return;

    delete pendingDownloadOrderEventsRef.current[orderId];
    applyDownloadOrderEvent(pendingEvent);
  }, [applyDownloadOrderEvent, checkoutOrderId]);

  useEffect(() => {
    if (!companyId || !checkoutOrderId || checkoutStatus !== "processing") {
      return;
    }

    let cancelled = false;
    let pollTimeout: number | undefined;

    const pollOrder = async () => {
      try {
        const { order } = await fetchDownloadOrder(companyId, checkoutOrderId);
        if (cancelled) return;

        if (order.status === "ready") {
          handleReady({
            orderId: order.id,
            orderNumber: order.orderNumber,
          });
          return;
        }
        if (order.status === "failed" || order.status === "expired") {
          handleFailed({
            orderId: order.id,
            message: order.errorMessage || undefined,
          });
          return;
        }
      } catch {
        // A later poll or socket event can still complete the checkout.
      }

      if (!cancelled) {
        pollTimeout = window.setTimeout(
          pollOrder,
          DOWNLOAD_ORDER_POLL_INTERVAL_MS
        );
      }
    };

    void pollOrder();

    return () => {
      cancelled = true;
      if (pollTimeout !== undefined) {
        window.clearTimeout(pollTimeout);
      }
    };
  }, [checkoutOrderId, checkoutStatus, companyId, handleFailed, handleReady]);

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;

    const isCurrentCheckoutOrder = (orderId?: string) =>
      !!orderId &&
      !!checkoutOrderIdRef.current &&
      String(orderId) === String(checkoutOrderIdRef.current);

    const shouldQueueDownloadOrderEvent = (orderId?: string) =>
      !!orderId && (!checkoutOrderIdRef.current || checkoutLoadingRef.current);

    const subscribe = () => {
      socketGet(`/${companyId}/sockets/subscribe`).catch(() => {
        /* cart count polling / fetches recover state; nothing to do here */
      });
    };

    const onCartUpdated = (...args: unknown[]) => {
      const payload = args[0] as CartUpdatedPayload | undefined;
      if (!payload?.companyId) return;
      if (String(payload.companyId) !== String(companyId)) return;
      dispatch(
        applyCartSnapshot({
          companyId: payload.companyId,
          count: payload.count,
          availableCount: payload.availableCount,
          items: payload.items,
          totalSizeBytes: payload.totalSizeBytes,
        })
      );
      if (!payload.items) {
        dispatch(fetchCartThunk(companyId));
      }
    };

    const onReady = (...args: unknown[]) => {
      const payload = args[0] as DownloadOrderReadyPayload | undefined;
      if (!payload) return;
      if (isCurrentCheckoutOrder(payload.orderId)) {
        handleReady(payload);
        return;
      }
      if (shouldQueueDownloadOrderEvent(payload.orderId)) {
        queuePendingDownloadOrderEvent({ type: "ready", payload });
      }
    };

    const onFailed = (...args: unknown[]) => {
      const payload = args[0] as DownloadOrderFailedPayload | undefined;
      if (!payload) return;
      if (isCurrentCheckoutOrder(payload.orderId)) {
        handleFailed(payload);
        return;
      }
      if (shouldQueueDownloadOrderEvent(payload.orderId)) {
        queuePendingDownloadOrderEvent({ type: "failed", payload });
      }
    };

    const onProgress = (...args: unknown[]) => {
      const payload = args[0] as DownloadOrderProgressPayload | undefined;
      if (!payload) return;
      if (isCurrentCheckoutOrder(payload.orderId)) {
        handleProgress(payload);
        return;
      }
      if (shouldQueueDownloadOrderEvent(payload.orderId)) {
        queuePendingDownloadOrderEvent({ type: "progress", payload });
      }
    };

    const onReconnect = () => {
      if (cancelled) return;
      subscribe();
      dispatch(fetchCartThunk(companyId));
    };

    socket.on("cart-updated", onCartUpdated);
    socket.on("download-order-ready", onReady);
    socket.on("download-order-failed", onFailed);
    socket.on("download-order-progress", onProgress);
    socket.on("reconnect", onReconnect);
    subscribe();

    return () => {
      cancelled = true;
      socket.off("cart-updated", onCartUpdated);
      socket.off("download-order-ready", onReady);
      socket.off("download-order-failed", onFailed);
      socket.off("download-order-progress", onProgress);
      socket.off("reconnect", onReconnect);
    };
  }, [
    companyId,
    dispatch,
    handleFailed,
    handleProgress,
    handleReady,
    queuePendingDownloadOrderEvent,
  ]);
}
